// EnderecosEntrega.js — o endereço de entrega da pessoa.
//
// ================= O QUE MUDOU, E POR QUE ERA GRAVE =================
//
// Até 02/09/2026 esta tela era um CRUD de vários endereços que gravava em
// `localStorage.insigne_enderecos` — e em mais lugar nenhum. O próprio arquivo
// trazia um TODO dizendo "plugar à API quando os endpoints existirem". Eles
// nunca existiram.
//
// O efeito prático não era de coins, era de entrega: o endereço do comprador de
// livro impresso morava no navegador dele. Trocar de aparelho, limpar o cache ou
// comprar pelo celular depois de cadastrar no computador significava endereço
// nenhum — e o frete e o pedido leem as colunas de `usuario_editoras`, que
// continuavam vazias.
//
// De quebra, o formulário não tinha campo de CEP e chamava o estado de `uf`,
// enquanto o backend conta o endereço como completo com
// `endereco, numero, bairro, cidade, estado, cep` (`campos_endereco_completo`).
// Mesmo que a gravação funcionasse, os 10 Insigne Coins do endereço nunca
// sairiam.
//
// ================= POR QUE UM ENDEREÇO, E NÃO UMA LISTA =================
//
// A lista era fachada: nada no sistema lê "vários endereços". O pedido, o frete
// e o cadastro leem as SETE COLUNAS de `usuario_editoras`, que comportam um
// endereço só. Manter a lista exigiria uma tabela nova, endpoints novos e uma
// escolha de "qual é o principal" no checkout — para entregar exatamente o que
// uma tela de um endereço entrega hoje.
//
// Preferi a tela que corresponde ao banco. Quando existir necessidade real de
// vários endereços, ela nasce junto com a tabela, e não antes.
//
// ================= O COMPLEMENTO É OBRIGATÓRIO =================
//
// Não é preciosismo de formulário: o backend recusa o PUT com
// `required_with:endereco`, porque sem complemento a transportadora devolve
// entrega em prédio e condomínio. Quem mora em casa escreve "sem complemento",
// e é isso que o placeholder sugere.
//
// ================= POR QUE O "+10" FICA NO CABEÇALHO =================
//
// Nos outros dados premiados o valor mora ao lado do próprio campo (Telefone,
// CPF, Curso). Aqui não dá: o endereço vale UM prêmio que depende de seis
// campos ao mesmo tempo, então não existe a linha ao lado da qual ele moraria.
// Pôr "+10" em cada uma prometeria 60 coins, e pagar por coluna faria o
// endereço valer mais que tudo o mais junto — o caminho mais barato de
// imprimir dinheiro no site.
//
// Então o prêmio é da SEÇÃO, e o cabeçalho é onde ele cabe: um bônus só, com a
// frase dizendo que o endereço conta como um dado e só rende completo.
import React, { useEffect, useState } from "react";
import useProfileData from "../../../hooks/userProfileData";
import useCoinsPerfil from "../../../hooks/useCoinsPerfil";
import { SectionCard, Field, TextInput, PrimaryButton, Toast, useToast } from "../ui/ProfileUI";
import { BonusCoins } from "../../ui/Formulario";
import { buscarCep } from "../../../utils/cep";

// A chave antiga. Só é lida, nunca mais escrita: serve para não fazer quem já
// tinha digitado o endereço digitar tudo de novo.
const STORAGE_KEY = "insigne_enderecos";

const VAZIO = {
  cep: "", endereco: "", numero: "", complemento: "",
  bairro: "", cidade: "", estado: "",
};

// As mesmas partes que `insigne.campos_endereco_completo` exige no servidor.
// Se as duas listas divergirem, a tela dirá "completo" e o servidor não pagará
// os coins — que é exatamente o tipo de divergência que gerou esta reescrita.
const OBRIGATORIOS = ["cep", "endereco", "numero", "bairro", "cidade", "estado"];

const soDigitos = (v) => String(v || "").replace(/\D/g, "");
const mascaraCep = (v) => {
  const d = soDigitos(v).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

/** O endereço que ficou no navegador da pessoa, se houver. */
const resgatarDoNavegador = () => {
  try {
    const lista = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    if (!Array.isArray(lista) || lista.length === 0) return null;
    // O "principal", ou o primeiro — a antiga tela deixava marcar um.
    const e = lista.find((x) => x?.principal) || lista[0];
    return {
      cep: "",
      endereco: e.endereco || "",
      numero: e.numero || "",
      complemento: e.complemento || "",
      bairro: e.bairro || "",
      cidade: e.cidade || "",
      // A tela antiga chamava de `uf`.
      estado: e.uf || e.estado || "",
    };
  } catch {
    return null;
  }
};

const EnderecosEntrega = () => {
  const { profileData, loading, updateProfile, fetchProfileData } = useProfileData();
  const { toast, fireToast } = useToast();
  const { premioDoCampo, recarregar: recarregarCoins } = useCoinsPerfil();

  const [form, setForm] = useState(VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [veioDoNavegador, setVeioDoNavegador] = useState(false);
  // "procurando" | "achou" | "nao-existe" | "falhou" | null
  const [estadoCep, setEstadoCep] = useState(null);

  // ===== O CEP PREENCHE O RESTO =====
  //
  // Rua, bairro, cidade e estado saem do CEP; número e complemento, não — esses
  // a pessoa sabe e o CEP não. Então só os quatro primeiros são preenchidos.
  //
  // SÓ O QUE VEIO É ESCRITO. CEP de cidade pequena é um só para o município e
  // volta sem rua nem bairro: escrever o vazio por cima apagaria o que a pessoa
  // já tinha digitado ali.
  const aoMudarCep = async (valor) => {
    const mascarado = mascaraCep(valor);
    setForm((f) => ({ ...f, cep: mascarado }));

    const digitos = soDigitos(mascarado);
    if (digitos.length !== 8) {
      setEstadoCep(null);
      return;
    }

    setEstadoCep("procurando");
    try {
      const achado = await buscarCep(digitos);
      if (!achado) {
        setEstadoCep("nao-existe");
        return;
      }
      setForm((f) => ({
        ...f,
        endereco: achado.endereco || f.endereco,
        bairro: achado.bairro || f.bairro,
        cidade: achado.cidade || f.cidade,
        estado: achado.estado || f.estado,
      }));
      setEstadoCep("achou");
    } catch {
      // Rede fora: o formulário continua preenchível à mão. Não é motivo para
      // travar a pessoa num campo que ela sabe responder.
      setEstadoCep("falhou");
    }
  };

  useEffect(() => {
    if (!profileData) return;

    const doServidor = {
      cep: mascaraCep(profileData.cep || ""),
      endereco: profileData.endereco || "",
      numero: profileData.numero || "",
      complemento: profileData.complemento || "",
      bairro: profileData.bairro || "",
      cidade: profileData.cidade || "",
      estado: (profileData.estado || "").toUpperCase(),
    };

    // Já existe endereço no servidor: ele manda, e o que estiver no navegador
    // não interessa mais.
    if (doServidor.endereco.trim()) {
      setForm(doServidor);
      return;
    }

    // Não existe. Se a pessoa tinha digitado no formulário antigo, o texto é
    // recuperado e apresentado PARA CONFIRMAR — não é salvo por conta própria.
    // Gravar sozinho um endereço que ninguém reviu é como entregar num lugar
    // que a pessoa não escolheu.
    const doNavegador = resgatarDoNavegador();
    if (doNavegador) {
      setForm(doNavegador);
      setVeioDoNavegador(true);
    }
  }, [profileData]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const faltando = OBRIGATORIOS.filter((c) => !String(form[c] || "").trim());
  const completo = faltando.length === 0 && String(form.complemento || "").trim() !== "";

  // Zero quando o servidor já pagou o endereço. Não é o `completo` acima que
  // decide: o formulário pode estar cheio na tela e ainda não ter sido salvo —
  // e há quem já tivesse o endereço no cadastro antes de a regra existir.
  const premioEndereco = premioDoCampo("endereco");
  const bonusEndereco = premioEndereco && !premioEndereco.recebido ? premioEndereco.coins : 0;

  const salvar = async () => {
    if (!String(form.endereco).trim()) {
      fireToast("Informe o endereço.", "error");
      return;
    }
    // O servidor recusa com 422 se vier endereço sem complemento. Barrar aqui
    // é só para a mensagem ser específica em vez de "não foi possível salvar".
    if (!String(form.complemento).trim()) {
      fireToast('Informe o complemento (ex.: apto, bloco, casa, ou "sem complemento").', "error");
      return;
    }

    try {
      setSalvando(true);
      await updateProfile({
        cep: soDigitos(form.cep),
        endereco: form.endereco.trim(),
        numero: String(form.numero).trim(),
        complemento: form.complemento.trim(),
        bairro: form.bairro.trim(),
        cidade: form.cidade.trim(),
        estado: form.estado.trim().toUpperCase(),
      });

      // O que estava no navegador cumpriu o papel e sai de cena: mantê-lo faria
      // a próxima visita oferecer de novo um texto que já está no servidor.
      if (veioDoNavegador) {
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* nada a fazer */ }
        setVeioDoNavegador(false);
      }

      if (fetchProfileData) await fetchProfileData();
      // O PUT já creditou o que estava pendente. Reler apaga o "+10" na hora, e
      // é esse sumiço que diz que os coins entraram — sem ele o prêmio só
      // apareceria na próxima visita, desligado do que a pessoa acabou de fazer.
      recarregarCoins();
      fireToast("Endereço salvo!");
    } catch {
      fireToast("Não foi possível salvar o endereço. Tente novamente.", "error");
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <SectionCard title="Endereço de Entrega"><p className="text-white/60">Carregando…</p></SectionCard>;
  }

  return (
    <SectionCard
      title="Endereço de Entrega"
      action={
        // Pago, fica só o recibo riscado; pendente, a frase inteira.
        premioEndereco ? (
          <span className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-xs text-white/50 text-right">
            {!premioEndereco.recebido && "O endereço completo rende"}
            <BonusCoins
              coins={premioEndereco.coins}
              recebido={premioEndereco.recebido}
              title={`O endereço completo rende ${bonusEndereco} Insigne Coins, uma vez só. Ele conta como UM dado: os coins entram quando todas as partes estiverem preenchidas, e preencher metade não paga metade.`}
            />
          </span>
        ) : null
      }
    >
      <Toast {...toast} />

      {veioDoNavegador && (
        <div className="mb-5 rounded-xl border border-insigne-orange/40 bg-insigne-orange/[0.07] p-4">
          <p className="text-sm text-white font-medium">
            <i className="bi bi-info-circle mr-1.5" />
            Encontramos um endereço salvo neste navegador
          </p>
          <p className="text-sm text-white/60 mt-1 leading-relaxed">
            Ele estava guardado só aqui e nunca chegou à sua conta. Confira os
            dados, complete o CEP e salve para valer.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5">
        <Field
          label="CEP"
          hint={
            estadoCep === "procurando" ? "Buscando endereço…"
            : estadoCep === "achou" ? "Endereço preenchido. Confira e complete o número."
            : estadoCep === "nao-existe" ? "CEP não encontrado. Confira os números."
            : estadoCep === "falhou" ? "Não deu para buscar agora. Preencha à mão."
            : "Digite o CEP e o endereço se preenche sozinho."
          }
        >
          <TextInput
            value={form.cep}
            onChange={(e) => aoMudarCep(e.target.value)}
            placeholder="00000-000"
            inputMode="numeric"
          />
        </Field>
        <Field label="Endereço">
          <TextInput value={form.endereco} onChange={set("endereco")} placeholder="Rua, avenida…" />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5 mt-5">
        <Field label="Número">
          <TextInput value={form.numero} onChange={set("numero")} placeholder="123" />
        </Field>
        <Field label="Complemento *" hint='Obrigatório. Sem ele a transportadora devolve entregas em prédio ou condomínio. Se não houver, escreva "sem complemento".'>
          <TextInput value={form.complemento} onChange={set("complemento")} placeholder="Apto, bloco, casa…" />
        </Field>
      </div>

      <Field label="Bairro" className="mt-5">
        <TextInput value={form.bairro} onChange={set("bairro")} placeholder="Insira seu bairro…" />
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-5 mt-5">
        <Field label="Cidade">
          <TextInput value={form.cidade} onChange={set("cidade")} placeholder="Insira sua cidade…" />
        </Field>
        <Field label="Estado">
          <TextInput
            value={form.estado}
            onChange={(e) => setForm((f) => ({ ...f, estado: e.target.value.toUpperCase().slice(0, 2) }))}
            maxLength={2}
            placeholder="UF"
          />
        </Field>
      </div>

      <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
        {/* A tela diz o que falta para o endereço CONTAR como completo, porque
            é a mesma condição que libera os Insigne Coins do cabeçalho — e um
            "+10" sem dizer o que falta é o que gerava a reclamação. O número
            não é repetido aqui: dois lugares com o mesmo valor é como eles
            passam a discordar. */}
        <p className="text-sm text-white/55">
          {completo ? (
            <>
              <i className="bi bi-check-circle-fill text-emerald-400 mr-1.5" />
              Endereço completo.
            </>
          ) : (
            <>
              Falta preencher: <span className="text-white/80">{faltando.join(", ") || "complemento"}</span>.
              {bonusEndereco > 0 && " Os coins entram quando o endereço estiver inteiro."}
            </>
          )}
        </p>
        <PrimaryButton onClick={salvar} disabled={salvando}>
          {salvando ? "Salvando…" : "Salvar endereço"}
        </PrimaryButton>
      </div>
    </SectionCard>
  );
};

export default EnderecosEntrega;
